/**
 * Page "Ma salle d'examen" — étudiant.
 * Affiche les sessions de brassage visibles + ma place dans la salle.
 * Possibilité de rechercher un autre étudiant (ex: pour vérifier où est un camarade).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search, ChevronRight, ArrowLeft, Users, AlertCircle, Check, Building2 } from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, BrassageSession } from '../services/portailApi';

const SessionDetail: React.FC<{ session: BrassageSession; onBack: () => void }> = ({ session, onBack }) => {
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState<{ matricule: string; nom_complet: string; classe: string; site_nom: string; salle_nom: string }[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showCamarades, setShowCamarades] = useState(false);

  const doSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQ.trim().length < 2) return;
    setSearching(true);
    setSearchError(null);
    try {
      const res = await portailApi.searchBrassage(session.id, searchQ.trim());
      setSearchResults(res.results);
    } catch (e: any) {
      setSearchError(e?.data?.detail || 'Erreur de recherche');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-3">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200">
        <ArrowLeft size={16} /> Retour à la liste
      </button>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <h2 className="font-semibold text-gray-100 text-base">{session.description}</h2>
        <div className="text-xs text-gray-400 mt-1">
          {session.nombre_etudiants} étudiants · {session.nombre_salles} salles
        </div>
      </div>

      {session.mon_placement ? (
        <div className="bg-gradient-to-br from-blue-900/40 to-purple-900/30 border border-blue-700 rounded-2xl p-5 text-center">
          <div className="text-xs text-blue-300 uppercase tracking-wider mb-2">Votre place</div>
          <div className="text-3xl font-bold text-white mb-1">{session.mon_placement.salle_nom}</div>
          <div className="text-base text-blue-100 flex items-center justify-center gap-1">
            <MapPin size={16} /> {session.mon_placement.site_nom}
          </div>
          <div className="text-xs text-gray-400 mt-3">Classe : {session.mon_placement.classe}</div>
        </div>
      ) : (
        <div className="bg-orange-900/30 border border-orange-700 rounded-xl p-4 text-sm text-orange-200">
          <AlertCircle size={11} className="inline mr-0.5" /> Vous n'apparaissez pas dans ce brassage. Contactez la scolarité si vous pensez que c'est une erreur.
        </div>
      )}

      {session.mon_placement && session.nb_camarades_meme_salle > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowCamarades(!showCamarades)}
            className="w-full p-3 flex items-center justify-between hover:bg-gray-800 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm text-gray-200 font-medium">
              <Users size={16} className="text-blue-400" />
              {session.nb_camarades_meme_salle} étudiant{session.nb_camarades_meme_salle > 1 ? 's' : ''} dans votre salle
            </span>
            <ChevronRight size={16} className={`text-gray-500 transition-transform ${showCamarades ? 'rotate-90' : ''}`} />
          </button>
          {showCamarades && (
            <div className="border-t border-gray-800 max-h-72 overflow-y-auto">
              {session.camarades_meme_salle.map((c) => (
                <div key={c.matricule || c.nom_complet} className="px-3 py-2 border-b border-gray-800/50 text-xs">
                  <div className="font-medium text-gray-200">{c.nom_complet}</div>
                  <div className="text-gray-500">{c.matricule} · {c.classe}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recherche d'un autre étudiant */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <div className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1">
          <Search size={12} /> Rechercher un autre étudiant
        </div>
        <form onSubmit={doSearch} className="flex gap-2">
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Nom ou matricule..."
            className="flex-1 px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100"
          />
          <button
            type="submit"
            disabled={searching || searchQ.trim().length < 2}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded font-medium"
          >
            {searching ? '…' : <Search size={14} />}
          </button>
        </form>
        {searchError && (
          <div className="mt-2 text-xs text-red-300">{searchError}</div>
        )}
        {searchResults !== null && (
          <div className="mt-3 space-y-2">
            {searchResults.length === 0 ? (
              <div className="text-xs text-gray-500 text-center py-2">Aucun résultat</div>
            ) : (
              searchResults.map((r) => (
                <div key={r.matricule || r.nom_complet} className="bg-gray-800/60 rounded p-2 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-gray-100">{r.nom_complet}</div>
                      <div className="text-gray-400">{r.matricule} · {r.classe}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-blue-300 font-mono font-semibold">{r.salle_nom}</div>
                      <div className="text-gray-500 text-[10px]">{r.site_nom}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function MaSalleExamen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<BrassageSession[]>([]);
  const [selected, setSelected] = useState<BrassageSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    portailApi.getBrassages()
      .then((res) => {
        if (mounted) {
          setSessions(res.sessions);
          // Si une seule session visible, ouvrir directement
          if (res.sessions.length === 1) {
            setSelected(res.sessions[0]);
          }
        }
      })
      .catch((e: any) => {
        if (e.status === 401) {
          portailApi.clearToken();
          navigate('/portail/login', { replace: true });
        } else {
          setError(e.message || 'Erreur de chargement');
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [navigate]);

  return (
    <PortailLayout showHeader title="Ma salle d'examen">
      <div className="space-y-3 mt-2">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : error ? (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-sm text-red-300">{error}</div>
        ) : selected ? (
          <SessionDetail session={selected} onBack={() => setSelected(null)} />
        ) : sessions.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center">
            <Building2 size={40} className="mx-auto mb-3 text-gray-600" />
            <p className="text-gray-300 font-medium">Aucun brassage publié</p>
            <p className="text-sm text-gray-500 mt-2">
              Les listes de répartition des salles seront publiées ici par la scolarité avant les examens.
            </p>
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-400 mb-1">
              {sessions.length} brassage{sessions.length > 1 ? 's' : ''} disponible{sessions.length > 1 ? 's' : ''}
            </div>
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className="w-full text-left bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl p-4 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-purple-400 flex-shrink-0" />
                      <h3 className="font-semibold text-gray-100 text-sm truncate">{s.description}</h3>
                    </div>
                    <div className="text-xs text-gray-400 mt-1.5 flex items-center gap-2 flex-wrap">
                      <span>{s.nombre_etudiants} étudiants</span>
                      <span>·</span>
                      <span>{s.nombre_salles} salles</span>
                    </div>
                    <div className="mt-2 text-xs">
                      {s.mon_placement ? (
                        <span className="inline-flex items-center gap-1 text-green-300">
                          <Check size={11} className="inline mr-0.5" /> Salle <strong>{s.mon_placement.salle_nom}</strong> ({s.mon_placement.site_nom})
                        </span>
                      ) : (
                        <span className="text-orange-300 italic inline-flex items-center gap-1"><AlertCircle size={11} /> Non placé dans ce brassage</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-500 flex-shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </>
        )}

        {!selected && (
          <button
            onClick={() => navigate('/portail/accueil')}
            className="w-full mt-4 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium"
          >
            ← Retour à l'accueil
          </button>
        )}
      </div>
    </PortailLayout>
  );
}
